import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useActivity } from '../App';

const apiCall = () => axios.create({
  baseURL: 'http://localhost:8080',
  headers: { Authorization: `Bearer ${localStorage.getItem('wb_admin_token')}` }
});

const RANK_META = {
  1: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: '#f59e0b', trophy: '🥇', label: 'Gold'   },
  2: { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: '#94a3b8', trophy: '🥈', label: 'Silver' },
  3: { color: '#cd7c3f', bg: 'rgba(205,124,63,0.12)',  border: '#cd7c3f', trophy: '🥉', label: 'Bronze' },
};

const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';

const getAvatarColor = (name = '') => {
  const colors = ['#6366f1','#8b5cf6','#ec4899','#06b6d4','#22c55e','#f59e0b','#ef4444'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const Leaderboard = () => {
  const { logActivity } = useActivity?.() || {};
  const [tab, setTab]           = useState('weekly');
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = useCallback(async (type = tab) => {
    setLoading(true);
    try {
      const res = await apiCall().get('/api/admin/leaderboard', { params: { type, size: 50 } });
      const list = res.data?.data || res.data || [];
      setEntries(Array.isArray(list) ? list : []);
      setLastUpdated(new Date());
    } catch {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { loadData(tab); }, [tab]);

  const handleTabChange = (t) => {
    setTab(t);
    loadData(t);
  };

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Stats</span> / <span className="current">Leaderboard</span></div>
          <h1>🏆 Leaderboard</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Top walkers ranked by distance</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {lastUpdated && (
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Last updated: {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button style={primaryBtnS} onClick={() => loadData(tab)}>🔄 Reload</button>
        </div>
      </div>

      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
        {['daily', 'weekly'].map(t => (
          <button
            key={t}
            onClick={() => handleTabChange(t)}
            style={{
              padding: '7px 24px',
              border: 'none',
              borderRadius: '7px',
              cursor: 'pointer',
              fontSize: '0.86rem',
              fontWeight: 600,
              transition: 'all 0.15s',
              background: tab === t ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'transparent',
              color: tab === t ? 'white' : 'var(--text-secondary)',
            }}
          >
            {t === 'daily' ? '📅 Daily' : '📆 Weekly'}
          </button>
        ))}
      </div>

      {/* Leaderboard List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Loading leaderboard…
        </div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          No leaderboard data available
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {entries.map((entry) => {
            const meta = RANK_META[entry.rank];
            const isTop3 = !!meta;
            const avatarColor = getAvatarColor(entry.name || '');

            return (
              <div
                key={entry.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  background: isTop3 ? meta.bg : 'var(--bg-card)',
                  border: `1px solid ${isTop3 ? meta.border : 'var(--border-color)'}`,
                  borderLeft: `4px solid ${isTop3 ? meta.border : 'var(--border-color)'}`,
                  borderRadius: '10px',
                  padding: '12px 18px',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(3px)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {/* Rank */}
                <div style={{ width: '40px', textAlign: 'center', flexShrink: 0 }}>
                  {isTop3 ? (
                    <span style={{ fontSize: '1.6rem' }}>{meta.trophy}</span>
                  ) : (
                    <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-muted)' }}>#{entry.rank}</span>
                  )}
                </div>

                {/* Avatar */}
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: avatarColor, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '0.82rem', fontWeight: 700,
                  color: 'white', flexShrink: 0,
                }}>
                  {getInitials(entry.name)}
                </div>

                {/* Name + City */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.name || 'Unknown'}
                  </div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                    {entry.city || 'Unknown city'}
                    {entry.email && <span style={{ marginLeft: '6px' }}>· {entry.email}</span>}
                  </div>
                </div>

                {/* Level Badge */}
                <div style={{ flexShrink: 0 }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700,
                    background: 'rgba(99,102,241,0.13)', color: 'var(--accent-primary)',
                  }}>
                    Lv {entry.level ?? 1}
                  </span>
                </div>

                {/* Streak */}
                <div style={{ flexShrink: 0, textAlign: 'center', minWidth: '64px' }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#f59e0b' }}>
                    🔥 {entry.streak ?? 0}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>streak</div>
                </div>

                {/* KM */}
                <div style={{ flexShrink: 0, textAlign: 'right', minWidth: '80px' }}>
                  <div style={{
                    fontSize: '1.1rem', fontWeight: 900,
                    color: isTop3 ? meta.color : 'var(--accent-primary)',
                  }}>
                    {(entry.totalKm ?? 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>km</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const primaryBtnS = { padding: '9px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' };

export default Leaderboard;
